import { Flow } from '@dynamic-flow/types';

export interface BindingValidationResult {
  isValid: boolean;
  missingRequiredBindings: string[];
  allBindings: Array<{
    name: string;
    required: boolean;
    fallback?: string;
    hasValue: boolean;
  }>;
}

function normalizeParamName(name: string): string {
  return name.toLowerCase().replace(/[-_]/g, '');
}

export function getBindingQueryParamValue(
  urlParams: URLSearchParams,
  bindingName: string
): string | null {
  const exact = urlParams.get(bindingName);
  if (exact !== null) {
    return exact;
  }

  const normalizedBinding = normalizeParamName(bindingName);
  for (const [key, value] of urlParams.entries()) {
    if (normalizeParamName(key) === normalizedBinding) {
      return value;
    }
  }
  return null;
}

/**
 * Validates URL query parameters against required bindings in flow fields
 */
export function validateUrlBindings(flow: Flow): BindingValidationResult {
  const urlParams = new URLSearchParams(window.location.search);
  const missingRequiredBindings: string[] = [];
  const allBindings: Array<{
    name: string;
    required: boolean;
    fallback?: string;
    hasValue: boolean;
  }> = [];

  // Extract all fields with bindings from flow nodes
  flow.nodes.forEach((node) => {
    if (node.data.questions) {
      node.data.questions.forEach((question) => {
        if (question.binding) {
          const queryParamValue = getBindingQueryParamValue(
            urlParams,
            question.binding.name
          );
          const hasValue = queryParamValue !== null;

          allBindings.push({
            name: question.binding.name,
            required: question.binding.required || false,
            fallback: question.binding.fallback,
            hasValue,
          });

          // Check if required binding is missing
          if (question.binding.required && !hasValue) {
            missingRequiredBindings.push(question.binding.name);
          }
        }
      });
    }
  });

  return {
    isValid: missingRequiredBindings.length === 0,
    missingRequiredBindings,
    allBindings,
  };
}

/**
 * Generates a user-friendly error message for missing bindings
 */
export function generateBindingErrorMessage(
  validation: BindingValidationResult
): string {
  if (validation.isValid) {
    return '';
  }

  const missingParams = validation.missingRequiredBindings.join(', ');
  return `Missing required URL parameters: ${missingParams}. Please contact the administrator for the correct form URL.`;
}
